import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  Users, UserPlus, Search, Trash2, Loader2, ShieldCheck,
  GraduationCap, Handshake, BookOpen, UserCog
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { value: 'student',    label: 'Étudiant',              icon: GraduationCap, color: 'bg-blue-100 text-blue-700' },
  { value: 'teacher',    label: 'Enseignant',             icon: BookOpen,      color: 'bg-green-100 text-green-700' },
  { value: 'partner',    label: 'Partenaire',             icon: Handshake,     color: 'bg-purple-100 text-purple-700' },
  { value: 'resp_admin', label: 'Resp. Administratif',   icon: UserCog,       color: 'bg-orange-100 text-orange-700' },
  { value: 'resp_peda',  label: 'Resp. Pédagogique',     icon: UserCog,       color: 'bg-yellow-100 text-yellow-700' },
  { value: 'admin',      label: 'Super Administrateur',  icon: ShieldCheck,   color: 'bg-red-100 text-red-700' },
];

const getRoleInfo = (role) => ROLES.find(r => r.value === role) || { label: role, color: 'bg-gray-100 text-gray-700' };

const getInitials = (first, last) => `${first?.[0] || ''}${last?.[0] || ''}`.toUpperCase();

export default function UsersPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ first_name: '', last_name: '', email: '', password: '', role: 'student' });
  const [creating, setCreating] = useState(false);

  // Load users
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const token = localStorage.getItem('hav_token');
      const res = await axios.get(`${API_URL}/users`, { headers: { Authorization: `Bearer ${token}` } });
      return res.data;
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (userId) => {
      const token = localStorage.getItem('hav_token');
      await axios.delete(`${API_URL}/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Utilisateur supprimé');
      setDeleteTarget(null);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
      setDeleteTarget(null);
    }
  });

  // Create user
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.first_name || !createForm.last_name || !createForm.email || !createForm.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    setCreating(true);
    try {
      const token = localStorage.getItem('hav_token');
      await axios.post(`${API_URL}/admin/create-user`, createForm, { headers: { Authorization: `Bearer ${token}` } });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(`Compte créé — identifiants envoyés par email`);
      setCreateOpen(false);
      setCreateForm({ first_name: '', last_name: '', email: '', password: '', role: 'student' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  // Stats per role
  const countByRole = ROLES.reduce((acc, r) => {
    acc[r.value] = users.filter(u => u.role === r.value).length;
    return acc;
  }, {});

  // Filtered list
  const filtered = users.filter(u => {
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    const term = search.toLowerCase();
    const matchesSearch = !term || 
      u.first_name?.toLowerCase().includes(term) ||
      u.last_name?.toLowerCase().includes(term) ||
      u.email?.toLowerCase().includes(term);
    return matchesRole && matchesSearch;
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Gestion des utilisateurs</h1>
          <p className="text-muted-foreground mt-1">{users.length} compte{users.length > 1 ? 's' : ''} au total</p>
        </div>
        <Button className="btn-active" onClick={() => setCreateOpen(true)} data-testid="create-user-btn">
          <UserPlus className="w-4 h-4 mr-2" />
          Créer un utilisateur
        </Button>
      </div>

      {/* Stats badges */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {ROLES.map(role => {
          const Icon = role.icon;
          return (
            <Card
              key={role.value}
              className={`border-0 shadow-sm cursor-pointer transition-all hover:shadow-md ${filterRole === role.value ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setFilterRole(filterRole === role.value ? 'all' : role.value)}
              data-testid={`filter-${role.value}`}
            >
              <CardContent className="p-4 text-center">
                <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl mb-2 ${role.color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <p className="text-2xl font-bold text-foreground">{countByRole[role.value] || 0}</p>
                <p className="text-xs text-muted-foreground leading-tight mt-1">{role.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Search & Filter */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par nom ou email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-users-input"
              />
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-role-select">
                <SelectValue placeholder="Tous les rôles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les rôles</SelectItem>
                {ROLES.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg">
            {filtered.length} utilisateur{filtered.length > 1 ? 's' : ''}
            {filterRole !== 'all' && ` · ${getRoleInfo(filterRole).label}`}
            {search && ` · "${search}"`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-40" />
              <p className="text-muted-foreground">Aucun utilisateur trouvé</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filtered.map(user => {
                const role = getRoleInfo(user.role);
                const Icon = role.icon || Users;
                const isProtected = user.email === 'ndedi.victoire@speak-move.com';
                return (
                  <div
                    key={user.id}
                    className="flex items-center justify-between px-6 py-4 hover:bg-muted/30 transition-colors"
                    data-testid={`user-row-${user.id}`}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      {/* Avatar */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${role.color}`}>
                        {getInitials(user.first_name, user.last_name)}
                      </div>
                      {/* Info */}
                      <div className="min-w-0">
                        <p className="font-semibold text-foreground truncate">
                          {user.first_name} {user.last_name}
                          {isProtected && <span className="ml-2 text-xs text-muted-foreground">(compte principal)</span>}
                        </p>
                        <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-4">
                      <span className={`hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${role.color}`}>
                        <Icon className="w-3 h-3" />
                        {role.label}
                      </span>
                      {user.created_at && (
                        <span className="hidden lg:block text-xs text-muted-foreground">
                          {new Date(user.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                      {!isProtected && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => setDeleteTarget(user)}
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading">Créer un utilisateur</DialogTitle>
            <DialogDescription>Les identifiants seront envoyés par email automatiquement.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 overflow-y-auto flex-1 pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Prénom *</Label>
                <Input value={createForm.first_name} onChange={e => setCreateForm({...createForm, first_name: e.target.value})} placeholder="Jean" required />
              </div>
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input value={createForm.last_name} onChange={e => setCreateForm({...createForm, last_name: e.target.value})} placeholder="Dupont" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={createForm.email} onChange={e => setCreateForm({...createForm, email: e.target.value})} placeholder="jean@email.com" required />
            </div>
            <div className="space-y-2">
              <Label>Mot de passe temporaire *</Label>
              <Input type="text" value={createForm.password} onChange={e => setCreateForm({...createForm, password: e.target.value})} placeholder="Min. 6 caractères" required minLength={6} />
            </div>
            <div className="space-y-2">
              <Label>Rôle *</Label>
              <Select value={createForm.role} onValueChange={v => setCreateForm({...createForm, role: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={creating} data-testid="create-user-submit">
                {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Créer le compte
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Supprimer l'utilisateur ?</DialogTitle>
            <DialogDescription>
              Cette action est irréversible. Le compte de <strong>{deleteTarget?.first_name} {deleteTarget?.last_name}</strong> ({deleteTarget?.email}) sera définitivement supprimé.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Annuler</Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate(deleteTarget?.id)}
              data-testid="confirm-delete-user"
            >
              {deleteMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
